import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  Box,
  Stack,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import axios from "axios";
import config from "../config.json";

const Dashboard = ({ userData, refreshUserData }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [countdown, setCountdown] = useState(-1);
  const [waiting, setWaiting] = useState(false);
  const inputRef = useRef(null);
  const optionalEmailRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCountdown((prevCountdown) =>
        prevCountdown > 0 ? prevCountdown - 1 : prevCountdown
      );
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    setOpenDialog(false);
  };

  const reportPolling = (id) => {
    setCountdown(4 * 60);
    let pollIntervals = [60000, 60000, 60000];
    let pollIndex = 1;

    const poll = () => {
      if (pollIndex >= 6) {
        setWaiting(true);
      }
      axios
        .get(`${config.apiBaseUrl}/reports/find?report_id=` + id)
        .then((response) => {
          if (response.data.report?.status === "complete") {
            setOpenDialog(false);
            toast.success("Report generated successfully");
            setWaiting(false);
            setTimeout(() => {
              window.open("/audit/report/" + id, "_blank");
            }, 1000);
          } else if (response.data.report?.status === "fail") {
            setOpenDialog(false);
            toast.error("Error generating report");
          } else {
            let interval = pollIntervals[pollIndex] || 20000;
            pollIndex = Math.min(pollIndex + 1, pollIntervals.length - 1);
            pollTimeoutRef.current = setTimeout(() => poll(), interval);
          }
        })
        .catch((error) => {
          console.error("Error fetching report status:", error);
          toast.error("Error fetching report status");
          setOpenDialog(false);
          setWaiting(false);
        });
    };

    pollTimeoutRef.current = setTimeout(() => poll(), pollIntervals[0]);
  };

  const validateUrl = (url) => {
    try {
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = "http://" + url;
      }
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const formatTime = (seconds) => {
    if (seconds === -1) {
      return "--:--";
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const startAudit = () => {
    if (!inputRef.current.value) {
      toast.error("Please enter a URL");
      return;
    }

    if (!validateUrl(inputRef.current.value)) {
      toast.error("Please enter a valid URL");
      return;
    }

    if (
      optionalEmailRef.current.value &&
      !validateEmail(optionalEmailRef.current.value)
    ) {
      toast.error("Please enter a valid email address");
      return;
    }

    console.log("Starting audit for", inputRef.current.value);
    setOpenDialog(true);
    if (
      !inputRef.current.value.startsWith("http://") &&
      !inputRef.current.value.startsWith("https://")
    ) {
      inputRef.current.value = "http://" + inputRef.current.value;
    }
    axios
      .post(`${config.apiBaseUrl}/init-audit`, {
        "e-mail": userData?.email || config.milkTubAdminEmail,
        url: inputRef.current.value,
        metadata: {
          client_mail: optionalEmailRef.current.value,
        },
      })
      .then((response) => {
        inputRef.current.value = "";
        optionalEmailRef.current.value = "";
        reportPolling(response.data.report?.report_id);
        refreshUserData();
        toast.success("Audit started successfully");
      })
      .catch((error) => {
        inputRef.current.value = "";
        optionalEmailRef.current.value = "";
        console.error("Error starting audit:", error);
        refreshUserData();
        toast.error("Error starting audit");
        setOpenDialog(false);
      });
  };

  return (
    <Container>
      <Box
        sx={{
          my: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "70vh",
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
          }}
        >
          <Typography
            variant="h4"
            color="#1d4ed8"
            component="h1"
            textAlign="center"
            gutterBottom
          >
            Get your website analyzed
          </Typography>
          <Stack
            direction={{ xs: "column" }}
            spacing={2}
            sx={{ width: "100%", maxWidth: 600 }}
            alignItems="center"
          >
            <TextField
              label="Enter the website here"
              placeholder="https://www.example.com"
              variant="outlined"
              fullWidth
              inputRef={inputRef}
              required
              sx={{
                height: "56px",
                width: { xs: "300px", sm: "600px", md: "700px" },
              }} // Adjust height to match button
            />
            <TextField
              label="Enter client email (optional)"
              placeholder="client@clientcompany.com"
              variant="outlined"
              fullWidth
              inputRef={optionalEmailRef}
              sx={{
                height: "56px",
                width: { xs: "300px", sm: "600px", md: "700px" },
              }} // Adjust height to match button
            />
            <Button
              variant="contained"
              color="primary"
              sx={{
                height: "56px",
                minWidth: "150px",
                maxWidth: "200px",
                width: { xs: "100%", sm: "auto" },
              }}
              onClick={startAudit}
            >
              Audit Now!
            </Button>
          </Stack>
        </Box>
      </Box>
      <Dialog maxWidth="md" open={openDialog} onClose={handleClose}>
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "15rem",
              padding: 2,
            }}
          >
            <Typography variant="h6" textAlign="center" gutterBottom>
              Analyzing your website
            </Typography>
            <CircularProgress />
            {waiting ? (
              <Typography variant="body1" textAlign={"center"}>
                Taking longer than expected <br />
                please wait...
              </Typography>
            ) : (
              <Typography variant="body1">
                Estimated time remaining:{" "}
                <span id="countdown">{formatTime(countdown)}</span> minutes
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Dashboard;
